// ثبت و ویرایش سرنخ (SCR-STAFF-960؛ US-ENG-001، 003): حداقل نام و موبایل (BR-ENG-001)، منبع از فهرست PAT، خدمت یا دسته مورد علاقه، یادداشت، رضایت تماس
// تبلیغاتی صریح (AC-ENG-001.2) و مسئول؛ پیش‌نمایش زنده تطبیق موبایل با مراجعه‌کننده فعلی و سرنخ باز ۳۰ روزه (AC-ENG-003.1، 003.2).
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { unwrap, type EngAssigneeOption, type EngLeadRequest, type EngLeadView, type EngOfferingOption } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { applyServerErrors, FormDialog, isValidMobile, keepDigitsOnly, latinDigits, MobileFieldRHF, normalizeMobile, SelectRHF, TextFieldRHF, zMobile, zRequiredText } from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useDebounced } from '../patients/common';
import { AssigneePicker, engKeys, OfferingPicker, useLeadSources } from './common';

const schema = z.object({
  fullName: zRequiredText(120),
  mobile: zMobile(),
  gender: z.string(),
  sourceId: z.string(),
  note: z.string().max(1000, { error: 'common:validation.max_length' }),
  consent: z.enum(['yes', 'no'], { error: 'eng:validation.consent_required' }),
  // AC-ENG-002.2: لینک گفتگوی دایرکت شبکه اجتماعی (اختیاری، فقط https).
  conversationUrl: z
    .string()
    .max(500, { error: 'common:validation.max_length' })
    .refine((value) => !value.trim() || /^https:\/\/\S+$/i.test(value.trim()), { error: 'eng:validation.conversation_url' }),
});
type LeadForm = z.input<typeof schema>;
const FIELDS = ['fullName', 'mobile', 'gender', 'sourceId', 'note', 'conversationUrl'] as const;

export interface LeadDialogProps {
  /** سرنخ برای ویرایش (تهی = ثبت). */
  lead?: EngLeadView;
  onClose: (saved?: EngLeadView) => void;
}

export function LeadDialog({ lead, onClose }: LeadDialogProps) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const consentId = useId();
  const sources = useLeadSources();
  const [submitError, setSubmitError] = useState<unknown>();
  const [owner, setOwner] = useState<EngAssigneeOption | null>(
    lead?.ownerMembershipId ? { membershipId: lead.ownerMembershipId, name: lead.ownerName ?? '', roles: [] } : null,
  );
  const [interest, setInterest] = useState<EngOfferingOption | null>(
    lead?.interestServiceId
      ? { kind: 'Service', id: lead.interestServiceId, name: lead.interestName ?? '', price: null }
      : lead?.interestCategoryId
        ? { kind: 'Category', id: lead.interestCategoryId, name: lead.interestName ?? '', price: null }
        : null,
  );
  const defaults = useMemo<LeadForm>(
    () => ({
      fullName: lead?.fullName ?? '',
      mobile: lead?.mobile ?? '',
      gender: lead?.gender ?? '',
      sourceId: lead?.sourceId ?? '',
      note: lead?.note ?? '',
      consent: lead ? (lead.marketingConsent ? 'yes' : 'no') : (undefined as unknown as 'yes'),
      conversationUrl: '',
    }),
    [lead],
  );
  const { control, handleSubmit, formState, setError } = useForm<LeadForm>({ resolver: zodResolver(schema), defaultValues: defaults });
  const mobile = useWatch({ control, name: 'mobile' });
  const digits = useDebounced(keepDigitsOnly(latinDigits(mobile ?? '')), 300);
  const normalized = isValidMobile(digits) ? (normalizeMobile(digits) ?? digits) : null;

  const match = useQuery({
    queryKey: [...engKeys.leads(session?.tenantId), 'match', normalized],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/leads/match', { params: { query: { mobile: normalized ?? '' } } })),
    enabled: !lead && normalized !== null,
    staleTime: 10_000,
  });

  const save = useMutation({
    mutationFn: (form: LeadForm) => {
      const body: EngLeadRequest = {
        fullName: form.fullName,
        mobile: form.mobile,
        gender: form.gender ? (form.gender as 'Female' | 'Male') : null,
        sourceId: form.sourceId || null,
        interestServiceId: interest?.kind === 'Service' ? interest.id : null,
        interestCategoryId: interest?.kind === 'Category' ? interest.id : null,
        branchId: lead?.branchId ?? null,
        note: form.note || null,
        marketingConsent: form.consent === 'yes',
        ownerMembershipId: owner?.membershipId ?? null,
        rowVersion: lead?.rowVersion ?? null,
        conversationUrl: !lead && form.conversationUrl.trim() ? form.conversationUrl.trim() : null,
      };
      return unwrap(
        lead
          ? tenantApi.PUT('/api/v1/engagement/leads/{id}', { params: { path: { id: lead.id } }, body })
          : tenantApi.POST('/api/v1/engagement/leads', { body, headers: { 'Idempotency-Key': idempotency.current() } }),
      );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (!lead) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: engKeys.all(session?.tenantId) });
      onClose(saved);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: FIELDS, codeFields: { 'PHX-ENG-0101': 'fullName' } });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const preview = match.data;

  return (
    <FormDialog
      open
      maxWidth="sm"
      screenId="SCR-STAFF-960-LEAD"
      titleKey={lead ? 'eng:lead_form.edit_title' : 'eng:lead_form.title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <TextFieldRHF control={control} name="fullName" labelKey="eng:lead_form.full_name" required autoFocus fullWidth inputProps={{ 'data-testid': 'lead-full-name' }} />
        <MobileFieldRHF control={control} name="mobile" labelKey="eng:lead_form.mobile" required />
        {preview && preview.patientMatch !== 'None' && (
          <Alert severity="info" data-testid="lead-match-patient">
            {preview.patientMatch === 'Single'
              ? t('eng:lead_form.match_single', { name: preview.patientName ?? '' })
              : t('eng:lead_form.match_multiple')}
          </Alert>
        )}
        {preview?.openLeadId && (
          <Alert severity="warning" data-testid="lead-match-open">
            {t('eng:lead_form.match_open_lead', { name: preview.openLeadName ?? '' })}
          </Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <SelectRHF
            control={control}
            name="gender"
            labelKey="eng:lead_form.gender"
            fullWidth
            options={[
              { value: '', labelKey: 'eng:common.unknown' },
              { value: 'Female', labelKey: 'eng:gender.Female' },
              { value: 'Male', labelKey: 'eng:gender.Male' },
            ]}
          />
          <SelectRHF
            control={control}
            name="sourceId"
            labelKey="eng:lead_form.source"
            fullWidth
            options={[{ value: '', labelKey: 'eng:common.unknown' }, ...(sources.data ?? []).map((source) => ({ value: source.id, label: source.name }))]}
          />
        </Stack>
        <OfferingPicker value={interest} onChange={setInterest} labelKey="eng:lead_form.interest" testId="lead-interest" categories />
        {!lead && (
          <TextFieldRHF
            control={control}
            name="conversationUrl"
            labelKey="eng:lead_form.conversation_url"
            helperKey="eng:lead_form.conversation_url_hint"
            fullWidth
            inputProps={{ dir: 'ltr', 'data-testid': 'lead-conversation-url' }}
          />
        )}
        <TextFieldRHF control={control} name="note" labelKey="eng:lead_form.note" helperKey="eng:common.no_clinical_hint" multiline minRows={2} fullWidth />
        <Controller
          control={control}
          name="consent"
          render={({ field, fieldState }) => (
            <FormControl error={Boolean(fieldState.error)} required>
              <FormLabel id={consentId}>{t('eng:lead_form.consent')}</FormLabel>
              <RadioGroup row aria-labelledby={consentId} value={field.value ?? ''} onChange={(event) => field.onChange(event.target.value)} data-testid="lead-consent">
                <FormControlLabel value="yes" control={<Radio />} label={t('eng:lead_form.consent_yes')} />
                <FormControlLabel value="no" control={<Radio />} label={t('eng:lead_form.consent_no')} />
              </RadioGroup>
              {fieldState.error?.message && <FormHelperText>{t(fieldState.error.message)}</FormHelperText>}
            </FormControl>
          )}
        />
        <AssigneePicker value={owner} onChange={setOwner} labelKey="eng:lead_form.owner" testId="lead-owner" />
      </Stack>
    </FormDialog>
  );
}
